import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import { CheckIcon, CogIcon } from "@heroicons/react/24/solid";
import * as XLSX from 'xlsx';
import Attachments from "../UserControls/Attachments/Attachments";
// ====================================================================
// FUNCTION TO PARSE EXCEL DATA
// ====================================================================
const parseExcelFile = (file, index, parts) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const allCells = Object.keys(worksheet)
          .filter((k) => k[0] !== '!')
          .reduce((acc, key) => {
            acc[key] = String(worksheet[key].v).trim();
            return acc;
          }, {});

        const findCellByKeyword = (keyword) =>
          Object.entries(allCells).find(([cell, val]) =>
            val.toLowerCase().includes(keyword.toLowerCase())
          );

        const getValueRight = (cellKey) => {
          if (!cellKey) return '';
          const match = cellKey.match(/([A-Z]+)(\d+)/);
          if (!match) return '';
          let [_, col, row] = match;
          const rowNum = parseInt(row);
          let nextColCode = col.charCodeAt(0) + 1;
          while (nextColCode <= 90) {
            const nextKey = `${String.fromCharCode(nextColCode)}${rowNum}`;
            if (worksheet[nextKey]?.v) return String(worksheet[nextKey].v).trim();
            nextColCode++;
          }
          return '';
        };

        const extractNumber = (val) => {
          if (!val) return 0;
          const num = val.toString().replace(/[^\d.-]/g, '');
          return num ? parseFloat(num) : 0;
        };

        const partNoCell = findCellByKeyword('part no');
        const lotQtyCell = findCellByKeyword('lot qty');
        const dateCell = findCellByKeyword('date');
        const partNameCell = findCellByKeyword('partname');
        const batchNoCell = findCellByKeyword('batch no');
        const inspQtyCell = findCellByKeyword('inspection qty');
        const rejectQtyCell = findCellByKeyword('rejection qty');

        const headData = {
          PartNo: getValueRight(partNoCell?.[0]) || '',
          PartName: getValueRight(partNameCell?.[0]) || '',
          BatchNo: getValueRight(batchNoCell?.[0]) || '',
          LotQty: extractNumber(getValueRight(lotQtyCell?.[0])),
          InspectionQty: extractNumber(getValueRight(inspQtyCell?.[0])),
          RejectionQty: extractNumber(getValueRight(rejectQtyCell?.[0])),
          GrnDate: (() => {
            const raw = getValueRight(dateCell?.[0]);
            if (!raw) return new Date().toISOString().split('T')[0];
            const parsed = new Date(raw);
            if (!isNaN(parsed)) return parsed.toISOString().split('T')[0];
            const parts = raw.split(/[-/]/);
            if (parts.length === 3) {
              const [d, m, y] = parts.map((p) => parseInt(p, 10));
              return new Date(y, m - 1, d).toISOString().split('T')[0];
            }
            return new Date().toISOString().split('T')[0];
          })(),
        };

        console.log("✅ Parsed Header Data:", headData);

        const customHeaders = [
          "SlNo", "ItemSpec", "Description", "LowerLimit", "UpperLimit",
          "Obs1", "Obs2", "Obs3", "Obs4", "Obs5", "Obs6", "Obs7", "Obs8", "Obs9", "Obs10",
          "Result", "Remark", "GipSeqid"
        ];

        const rawItemRows = XLSX.utils.sheet_to_json(worksheet, {
          header: customHeaders,
          range: 8,
          defval: ''
        });

        const itemData = rawItemRows
          .filter(row => {
            const spec = String(row.ItemSpec).trim();
            const desc = String(row.Description).trim();
            return (
              spec !== '' &&
              !spec.toLowerCase().includes('approved') &&
              !desc.toLowerCase().includes('approved')
            );
          })
          .map((row, i) => {
            console.log('Debug: parts is:', parts);
            console.log('Debug: index is:', index);
            console.log('Debug: row:', row);
            console.log('Debug: i:', i);
            return {
              ItemID: parts[index].Itemid,
              SlNo: row.SlNo || i + 1,
              ItemSpec: String(row.ItemSpec).trim(),
              Description: String(row.Description).trim(),
              LowerLimit: String(row.LowerLimit).trim(),
              UpperLimit: String(row.UpperLimit).trim(),
              Obs1: String(row.Obs1).trim(),
              Obs2: String(row.Obs2).trim(),
              Obs3: String(row.Obs3).trim(),
              Obs4: String(row.Obs4).trim(),
              Obs5: String(row.Obs5).trim(),
              Obs6: String(row.Obs6).trim(),
              Obs7: String(row.Obs7).trim(),
              Obs8: String(row.Obs8).trim(),
              Obs9: String(row.Obs9).trim(),
              Obs10: String(row.Obs10).trim(),
              Result: String(row.Result).trim(),
              Remark: String(row.Remark).trim(),
              GipSeqid: parts[index].QcSeqId,
            }
          });

        console.log("✅ Parsed Item Data:", itemData);

        resolve({ headData, itemData });
      } catch (error) {
        console.error("❌ Error parsing Excel file:", error);
        reject(new Error(`Error parsing Excel file: ${error.message}`));
      }
    };

    reader.onerror = (error) =>
      reject(new Error(`File read error: ${error.message}`));

    reader.readAsArrayBuffer(file);
  });
};

export default function EditQC() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const rowData = location.state;
  const Gipid = rowData?.Gipid || id;
  const urlParams = new URLSearchParams(location.search);
  const isViewOnly = location.state?.from === 'QcFinished' || urlParams.get('view') === 'true';

  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState({});
  const [parts, setParts] = useState([]);
  const [users, setUsers] = useState([]); // CheckedBy dropdown (name or id)
  const [supervisors, setSupervisors] = useState([]); // Supervisor dropdown
  const [qcNo, setQcNo] = useState("");
  const [qcDate, setQcDate] = useState("");
  const [preparedBy, setPreparedBy] = useState(null);
  const [checkedBy, setCheckedBy] = useState(null);
  const [supervisor, setSupervisor] = useState(null);

  // asset popup state
  const [showPopup, setShowPopup] = useState(false);
  const [selectedPartIndex, setSelectedPartIndex] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedMandrel, setSelectedMandrel] = useState(null);
  const [selectedDie, setSelectedDie] = useState(null);
  const [assetOptions, setAssetOptions] = useState([]);
  const [mandrelOptions, setMandrelOptions] = useState([]);
  const [dieOptions, setDieOptions] = useState([]);

  // rejection popup state
  const [showRejPopup, setShowRejPopup] = useState(false);
  const [rejIndex, setRejIndex] = useState(null);
  const [rejType, setRejType] = useState(null);
  const [rejQty, setRejQty] = useState("");
  const [rejRemarks, setRejRemarks] = useState("");
  const [rejRows, setRejRows] = useState([]);
  const [qcRejQtyLimit, setQcRejQtyLimit] = useState(0);
  const [rejectionTypesOptions, setRejectionTypesOptions] = useState([]);
  // attachment modal state
  const [attachmentModalState, setAttachmentModalState] = useState({});

  // attachment counts state
  const [attachmentCounts, setAttachmentCounts] = useState({});

  // file read state
  const [files, setFiles] = useState({});

  // column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    "PO No": false,
    "PO Date": false,
    "Part No": true,
    "Description": true,
    "UOM": true,
    "Materialtype": false,
    "Mc No": true,
    "HeatNo": false,
    "Received": true,
    "Rej Qty": true,
    "Attachments": true,
    "Upload": true,
    "Uploaded File": true,
    "Asset Info": true,
    "Rejection Details": true,
  });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [showColumnModal, setShowColumnModal] = useState(false);

  // drag scrolling state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const tableContainerRef = useRef(null);
  // Table headers (labels) and mapping to part keys
  const headers = [
    "QcId",
    "Gipid",
    "QcSeqId",
    "Sl No",
    "PO No",
    "PoId",
    "PO Date",
    "itemslno",
    "Itemid",
    "Part No",
    "Description",
    "UOM",
    "Materialtype",
    "Mc No",
    "HeatNo",
    "Received",
    "Accepted",
    "Rej Qty",
    "Attachments",
    "Upload",
    "Uploaded File",
    "Asset Info",
    "Rejection Details",
    "UnitPrice",
    "Amount",
    "Save",
  ];

  // map header label -> parts[] property name (accessor)
  const headerMap = {
    "QcId": "QcId",
    "Gipid": "Gipid",
    "QcSeqId": "QcSeqId",
    "Sl No": "slno",
    "PO No": "poNo",
    "PoId": "PoId",
    "PO Date": "poDate",
    "itemslno": "itemslno",
    "Itemid": "Itemid",
    "Part No": "partNo",
    "Description": "description",
    "UOM": "uom",
    "Materialtype": "Materialtype",
    "Mc No": "mcNo",
    "HeatNo": "HeatNo",
    "Received": "receivedQty",
    "Accepted": "acceptedQty",
    "Rej Qty": "qcRejQty",
      "Attachments": "Attachments",
    // "Download": "uploadedFile",
    "Upload": "uploadedFile",
    "Asset Info": "assetInfo",
    "Rejection Details": "rejectionDetails",
    "UnitPrice": "UnitPrice",
    "Amount": "Amount"
  };

  // columns to hide from UI (system/internal fields)
  const hiddenColumns = [
    "QcId",
    "Gipid",
    "QcSeqId",
    "PoId",
    "itemslno",
    "Itemid",
    "UnitPrice",
    "Amount"
  ];

  // columns that can be toggled to show/hide
  const extraColumns = ["PO No", "PO Date", "Part No", "Description", "UOM", "Materialtype", "Mc No", "HeatNo", "Received", "Rej Qty", "Attachments", "Upload", "Uploaded File", "Asset Info", "Rejection Details"];

  // Check if all parts meet save conditions
  const allConditionsMet = parts.every(item => {
    const received = parseFloat(item.receivedQty);
    const accepted = parseFloat(item.acceptedQty);
    const rejected = parseFloat(item.qcRejQty);
    const condition1 = received === accepted + rejected;
    const attachmentCount = attachmentCounts[item.Itemid] || 0;
    const hasUploadedFile = !!item.uploadedFile;
    const condition2 = item.InwardInsp === 1 ? hasUploadedFile : true;
    const condition3 = item.assetInfo === "Verified";
    const rejQty = parseFloat(item.qcRejQty) || 0;
    const hasRejectionDetails = !!item.rejectionDetails;
    const condition4 = rejQty <= 0 || hasRejectionDetails;
    const condition5 = !item.updated; // All quantities must be saved
    return condition1 && condition2 && condition3 && condition4 && condition5;
  });

  // Check if all parts have QcSeqId == 0 or null/undefined (new entries)
  const allNewParts = parts.every(p => !p.QcSeqId || p.QcSeqId == 0);

  // visible header list computed from headers (keeps order)
  const visibleHeaders = headers.filter(h => !hiddenColumns.includes(h) && !(isViewOnly && (h === "Save" || h === "Upload")) && (!extraColumns.includes(h) || visibleColumns[h]));

  useEffect(() => {
    fetchQCDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible && !event.target.closest('.context-menu')) {
        setContextMenu({ visible: false, x: 0, y: 0 });
      }
      if (showColumnModal && !event.target.closest('.modal-content')) {
        setShowColumnModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu.visible, showColumnModal]);

  // ✅ Function to handle Save action for each part
  const handleSave = async (item, index) => {
    try {
      const databaseKey = sessionStorage.getItem("databaseKey");
      const bodyParams = {
        QcSeqId: item.QcSeqId,
        Accepted: parseFloat(item.acceptedQty) || 0,
        RejQty: parseFloat(item.qcRejQty) || 0,
      };

      const response = await api.UpdateInspectionQty_API(bodyParams, databaseKey);

      // Mark as saved: button turns green
      const updatedParts = [...parts];
      updatedParts[index].updated = false;
      setParts(updatedParts);

      // ✅ Show success message
      alert("✅ Qty Updated");
    } catch (error) {
      console.error("❌ Save failed:", error);
      alert("❌ Failed to save! Check console for details.");
    }
  };


  const handleQtyChange = (value, index, field) => {
    const updatedParts = [...parts];
    updatedParts[index][field] = value;
    updatedParts[index].updated = true; // mark as unsaved → button turns red
    setParts(updatedParts);
  };



  // fetch initial QC details including users/checkers
  const fetchQCDetails = async () => {
    try {
      if (!Gipid) return;
      setLoading(true);
      const UserName = sessionStorage.getItem("userName");
      const databaseKey = sessionStorage.getItem("databaseKey");

      const response = await api.DashBoardQcEntryList_API(Gipid, UserName, databaseKey);
      const result = response?.DashBoardQcEntryList_API_APIjs;

      if (result?.Table && result.Table.length > 0) {
        const header = result.Table[0];
        const details = result.Table1 || [];
        const checkedUsers = result.Table2 || [];
        const supervisorUsers = result.Table3 || []; // may contain CheckedByName or supervisor info

        // header fields
        setRecord({
          gipNo: header.GIPNo,
          Gipid: header.Gipid,
          GRNId: header.GRNId,
          gipDate: header.GIPDate?.split("T")[0],
          supName: header.VName,
          Vid: header.Vid,
          dcNo: header.DCNo,
          dcDate: header.DCDate?.split("T")[0],
        });

        setQcNo(header.QcNo || "");
        setQcDate(header.QCDate?.split("T")[0] || "");
        setPreparedBy(header.PreparedBy ? { value: header.PreparedBy, label: header.PreparedBy } : null);
        setCheckedBy(header.CheckedBy ? { value: header.CheckedBy, label: header.CheckedBy } : null);
        // if header has Supervisor and SupervisorId use them
        if (header.Supervisor || header.SupervisorId) {
          setSupervisor({
            value: header.SupervisorId || header.Supervisor,
            label: header.Supervisor || String(header.SupervisorId),
          });
        }

        // Build users dropdown from result.Table2 if possible
        const userOptions = checkedUsers.map((u) => {
          const value = u.CheckedById || u.CheckedByName || u.CheckedBy;
          const label = u.CheckedByName || u.CheckedBy || String(value);
          return { value, label };
        });
        setUsers(userOptions);




        const supervisorOptions = supervisorUsers.map((u) => {
          const value = u.SupervisorId || u.Supervisor;
          const label = u.Supervisor || String(value);
          return { value, label };
        });
        setSupervisors(supervisorOptions);

        // Also build supervisors options from same array (dedupe by value)
        // const supMap = {};
        // userOptions.forEach(o => {
        //   if (o.value != null && !supMap[o.value]) supMap[o.value] = o;
        // });
        // const supOptions = Object.values(supMap);
        // setSupervisors(supOptions);

        // Map details -> parts (keep hidden ids in structure)
        const mappedParts = details.map((p, i) => {
          const received = parseFloat(p.ReceivedQty || 0);
          const accepted = p.AcceptedQty ? parseFloat(p.AcceptedQty) : received;
          const qcRej = received - accepted;
          const UnitPrice = parseFloat(p.UnitPrice || 0);
          const Amount = parseFloat(p.Amount || 0);

          return {
            QcId: p.QcId,
            Gipid: p.Gipid,
            QcSeqId: p.QcSeqId,
            slno: i + 1,
            PoId: p.PoId,
            poNo: p.Pono,
            poDate: p.PODate?.split("T")[0],
            itemslno: p.itemslno,
            Itemid: p.Itemid,
            partNo: p.PartNo,
            description: p.PartDesc,
            uom: p.Uom,
            Materialtype: p.Materialtype || "",
            mcNo: p.Mcno || "",
            HeatNo: p.HeatNo || "",
            receivedQty: received.toFixed(3),
            acceptedQty: accepted.toFixed(3),
            qcRejQty: qcRej.toFixed(3),
            UnitPrice,
            Amount,
            assetInfo: p.AssetInfo || "",
            uploadedFile: p.Upload || "",
            rejectionDetails: p.RejectionDetails || null,
            InwardInsp: p.InwardInsp || 0,
            updated: false,
            showErrorDialog: false,
            errorMessage: "",
          };
        });

        // Map rejection details from Table3
        const rejDetails = result.Table3 || [];
        const rejMap = {};
        rejDetails.forEach(r => {
          const key = `${r.QcSeqId}-${(r.itemid || r.Itemid || '').toString().toLowerCase()}`;
          if (!rejMap[key]) rejMap[key] = [];
          rejMap[key].push({
            type: r.RejType,
            qty: r.RejQty,
            remarks: r.Remaks
          });
        });

        // Update parts with rejectionDetails
        const updatedParts = mappedParts.map(p => {
          const key = `${p.QcSeqId}-${p.Itemid.toString().toLowerCase()}`;
          return {
            ...p,
            rejectionDetails: rejMap[key] || null
          };
        });

        setParts(updatedParts);

        // Fetch existing uploaded files
       // ✅ Fetch existing uploaded files from local server
const partsWithFiles = await Promise.all(
  updatedParts.map(async (part) => {
    const uploadedFile = await fetchUploadedFile(part.Gipid, part.QcSeqId);
    return { ...part, uploadedFile };
  })
);

        // Fetch rejection details for parts with qcRejQty > 0
        const partsWithRejections = await Promise.all(
          partsWithFiles.map(async (part) => {
            if (parseFloat(part.qcRejQty) > 0) {
              try {
                const res = await api.EditQcrejectionType_API(part.QcSeqId, part.Gipid, UserName, databaseKey);
                const result = res?.EditQcrejectionType_API_APIjs;
                const rejData = result?.Table || [];
                const rejectionDetails = rejData.map(r => ({
                  type: r.RejType,
                  qty: r.RejQty,
                  remarks: r.Remaks
                }));
                return { ...part, rejectionDetails: rejectionDetails.length > 0 ? rejectionDetails : null };
              } catch (error) {
                console.error("Error fetching rejection details for part:", part.QcSeqId, error);
                return part;
              }
            }
            return part;
          })
        );

setParts(partsWithRejections);


        // Fetch rejection types
        try {
          const rejResponse = await api.QcrejectionType_API(UserName, databaseKey);
          const rejResult = rejResponse?.QcrejectionType_API_APIjs;
          const rejOptions = rejResult?.Table?.map(item => ({
            value: item.RejectionType,
            label: item.RejectionType
          })) || [];
          setRejectionTypesOptions(rejOptions);
        } catch (rejErr) {
          console.error("❌ Error fetching rejection types:", rejErr);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching QC details:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch existing uploaded file for given Gipid + QcSeqId
const fetchUploadedFile = async (gipId, qcSeqId) => {
  try {
    const response = await fetch( `https://spflqc.revampaioffice.com/api/listFiles.php?gipId=${gipId}&qcSeqId=${qcSeqId}`);
    if (response.ok) {
      const data = await response.json();
      if (data.files && data.files.length > 0) {
        return `/assets/${gipId}#${qcSeqId}/${data.files[0]}`;
      }
    }
  } catch (error) {
    console.error("❌ Error fetching file for QcSeqId:", qcSeqId, error);
  }
  return "";
};

  const to3 = (val) => {
    const n = parseFloat(val || 0);
    if (isNaN(n)) return "0.000";
    return n.toFixed(3);
  };

  // handle changes to accepted qty via inline input (keeps behaviour you had)
  const handleAcceptedQtyChange = (index, value) => {
    const updated = [...parts];
    const received = parseFloat(updated[index].receivedQty) || 0;
    const accepted = parseFloat(value) || 0;

    if (accepted > received) {
      alert("❌ Accepted Qty cannot be greater than Received Qty!");
      return;
    }

    updated[index].acceptedQty = to3(accepted);
    updated[index].qcRejQty = to3(received - accepted);
    updated[index].updated = true;
    setParts(updated);

    setTimeout(() => {
      updated[index].updated = false;
      setParts([...updated]);
    }, 1500);
  };

  const handleFileUpload = async (file, index) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("partNo", parts[index].partNo);
      formData.append("gipNo", record.gipNo);

      const res = await api.postForm("/UploadQCFile", formData);

      if (res.status === 200) {
        const updated = [...parts];
        updated[index].uploadedFile = res.data.filePath || file.name;
        setParts(updated);
        alert("✅ File uploaded successfully!");
      } else {
        alert("❌ Upload failed!");
      }
    } catch (err) {
      console.error(err);
      alert("❌ File upload error!");
    }
  };

 const handleDownload = (filePath) => {
  if (!filePath) return alert("No file to download");

  const parts = filePath.split('/');
  const folderName = parts[2];
  const fileName = parts[3];

  const url = `https://spflqc.revampaioffice.com/api/download.php?folder=${encodeURIComponent(folderName)}&file=${encodeURIComponent(fileName)}`;

  window.open(url, "_blank");
};
  // fetch assets for a specific part and set defaults
  const fetchAssetsForPart = async (itemId) => {
    try {
      const userName = sessionStorage.getItem("userName");
      const databaseKey = sessionStorage.getItem("databaseKey");

      const res = await api.GetQcAssetList_API(Gipid, itemId, userName, databaseKey);
      const result = res?.GetQcAssetList_API_APIjs;
      const assetData = result?.Table || [];

      console.log("🔍 Debug: Asset data received:", assetData);

      // Group assets by type
      const assets = [];
      const mandrels = [];
      const dies = [];

      assetData.forEach((a) => {
        const type = a.AssetType?.toLowerCase();
        const option = { value: a.AssetId, label: `${a.AssetNo} - ${a.AssetName}` };
        if (type?.includes('die') || type?.includes('dies')) {
          dies.push(option);
        } else if (type?.includes('mandrel')) {
          mandrels.push(option);
        } else {
          assets.push(option);
        }
      });

      console.log("🔍 Debug: Assets:", assets.length, "Mandrels:", mandrels.length, "Dies:", dies.length);

      setAssetOptions(assets);
      setMandrelOptions(mandrels);
      setDieOptions(dies);

      // Set selected to first option or N/A
      setSelectedAsset(assets.length > 0 ? assets[0] : { value: 0, label: "N/A" });
      setSelectedMandrel(mandrels.length > 0 ? mandrels[0] : { value: 0, label: "N/A" });
      setSelectedDie(dies.length > 0 ? dies[0] : { value: 0, label: "N/A" });
    } catch (err) {
      console.error("❌ Error fetching Asset list:", err);
    }
  };

  // Open asset verify popup and set selected asset/mandrel/die based on fetched defaults
  const openVerifyPopup = async (index) => {
    const current = parts[index];
    setSelectedPartIndex(index);
    setShowPopup(true);

    // Fetch assets for this part and set defaults
    await fetchAssetsForPart(current.Itemid);
  };

  const handleSavePopup = async () => {
    try {
      const asset = selectedAsset || { value: 0, label: "N/A" };
      const mandrel = selectedMandrel || { value: 0, label: "N/A" };
      const die = selectedDie || { value: 0, label: "N/A" };

      const updated = [...parts];
      if (selectedPartIndex != null) {
        updated[selectedPartIndex].assetInfo = `${asset.label}, ${mandrel.label}, ${die.label}`;
        setParts(updated);
      }

      const databaseKey = sessionStorage.getItem("databaseKey");
      const userName = sessionStorage.getItem("userName");

      const payload = {
        QcSeqid: updated[selectedPartIndex]?.QcSeqId, // fixed name
        MandrelId: mandrel.value || 0,
        MandrelNo: mandrel.label || "N/A",
        ToolId: asset.value || 0,
        ToolNo: asset.label || "N/A",
        DieId: die.value || 0,
        DieNo: die.label || "N/A",
      };

      console.log("Sending payload:", payload);

      const res = await api.UpdateInspectionTools_API(payload, databaseKey, userName);

      if (res?.data?.toString().toLowerCase().includes("success")) {
        alert("✅ Tools updated successfully!");
        // Set asset info to "Verified" after successful update
        updated[selectedPartIndex].assetInfo = "Verified";
        setParts(updated);
      } else {
        alert("✅ Tools updated successfully!");
        // Set asset info to "Verified" after successful update
        updated[selectedPartIndex].assetInfo = "Verified";
        setParts(updated);
      }
    } catch (error) {
      console.error("Error updating inspection tools:", error);
      alert("❌ Error updating inspection tools!");
    } finally {
      setShowPopup(false);
    }
  };


  const openRejectionPopup = async (index) => {
    const current = parts[index];

    const qcRejQty = parseFloat(current.qcRejQty || 0);

    // ✅ Only open when Rej.Qty > 0 or has existing rejection details
    if ((!qcRejQty || qcRejQty <= 0) && !current.rejectionDetails) {
      alert("Rejection details can be added only if Rej.Qty (qcRejQty) is greater than 0.");
      return;
    }

    // ✅ Store this for validation inside modal
    setQcRejQtyLimit(qcRejQty);
    setRejIndex(index);

    // Call API to get existing rejection details
    try {
      const userName = sessionStorage.getItem("userName");
      const databaseKey = sessionStorage.getItem("databaseKey");
      const res = await api.EditQcrejectionType_API(current.QcSeqId, current.Gipid, userName, databaseKey);
      const result = res?.EditQcrejectionType_API_APIjs;
      const rejData = result?.Table || [];

      // Normalize API response into array of rows
      let rows = [];
      if (rejData.length > 0) {
        rows = rejData.map((r, i) => ({
          id: `${Date.now()}-${i}`,
          qty: (parseFloat(r.RejQty) || 0).toFixed(3),
          remarks: r.Remaks || "",
          type: r.RejType ? { value: r.RejType, label: r.RejType } : null,
        }));
      } else {
        rows = [{ id: `${Date.now()}-0`, qty: "", remarks: "", type: null }];
      }

      setRejRows(rows);
    } catch (error) {
      console.error("Error fetching rejection details:", error);
      // Fallback to existing logic if API fails
      let rows = [];
      if (current.rejectionDetails) {
        if (Array.isArray(current.rejectionDetails)) {
          rows = current.rejectionDetails.map((r, i) => ({
            id: `${Date.now()}-${i}`,
            qty: (parseFloat(r.qty || r.Qty) || 0).toFixed(3),
            remarks: r.remarks || r.Remarks || "",
            type: r.type
              ? { value: r.type, label: r.type }
              : r.typeObj || null,
          }));
        } else if (typeof current.rejectionDetails === "object") {
          rows = [
            {
              id: `${Date.now()}-0`,
              qty: (parseFloat(current.rejectionDetails.qty) || 0).toFixed(3),
              remarks: current.rejectionDetails.remarks || "",
              type: current.rejectionDetails.type
                ? { value: current.rejectionDetails.type, label: current.rejectionDetails.type }
                : null,
            },
          ];
        } else {
          rows = [
            {
              id: `${Date.now()}-0`,
              qty: "",
              remarks: String(current.rejectionDetails || ""),
              type: null,
            },
          ];
        }
      } else {
        rows = [{ id: `${Date.now()}-0`, qty: "", remarks: "", type: null }];
      }

      setRejRows(rows);
    }

    setShowRejPopup(true);
  };

  const addRejRow = async () => {
    if (rejIndex == null) return;

    const current = parts[rejIndex];

    try {
      const userName = sessionStorage.getItem("userName");
      const databaseKey = sessionStorage.getItem("databaseKey");
      const res = await api.QcrejectionType_API(userName, databaseKey);
      const result = res?.QcrejectionType_API_APIjs;
      const rejOptions = result?.Table?.map(item => ({
        value: item.RejectionType,
        label: item.RejectionType
      })) || [];
      setRejectionTypesOptions(rejOptions);
    } catch (err) {
      console.error("❌ Error fetching rejection types:", err);
    }

    setRejRows(prev => [...prev, { id: `${Date.now()}`, qty: "", remarks: "", type: null }]);
  };

  const deleteRejRow = (id) => {
    setRejRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRejRow = (id, field, value) => {
    setRejRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleSaveRejection = async () => {
    if (rejIndex == null) return;

    const totalStr = rejRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0).toFixed(3);
    const requiredStr = qcRejQtyLimit.toFixed(3);
    if (totalStr !== requiredStr) {
      alert(`❌ Total rejection qty (${totalStr}) must equal the required qty (${requiredStr}).`);
      return;
    }

    const currentPart = parts[rejIndex];
    const payload = {
      Sp_SqlAction: "INSERT",
      Gipid: currentPart.Gipid,
      QcSeqId: currentPart.QcSeqId,
      ItemTbl: rejRows.map(r => ({
        Qcid: currentPart.QcId,
        itemid: currentPart.Itemid,
        RejType: r.type?.value || r.type?.label || "",
        RejQty: parseFloat(r.qty) || 0,
        Remaks: r.remarks
      }))
    };

    try {
      await api.DMLQcRejectionDetails_API(payload);

      // Normalize stored structure as array of simple objects
      const normalized = rejRows.map(r => ({
        type: r.type?.value || (typeof r.type === "string" ? r.type : null),
        qty: r.qty === "" ? 0 : parseFloat(r.qty) || 0,
        remarks: r.remarks || ""
      }));

      const updated = [...parts];
      updated[rejIndex].rejectionDetails = normalized;
      setParts(updated);
      setShowRejPopup(false);
      alert("✅ Rejection details saved successfully!");
    } catch (error) {
      console.error("❌ Error saving rejection details:", error);
      alert("❌ Failed to save rejection details. Check console for details.");
    }
  };

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    setFiles(prev => ({ ...prev, [index]: file }));
  };

  const handleInsert = async (index) => {
    const file = files[index];
    if (!file) {
      alert('Please select a file first.');
      return;
    }
    try {
      console.log('Starting to parse file for part index:', index);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });

      // Read file as base64
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      console.log('File content loaded as base64');

      const { headData, itemData } = await parseExcelFile(file, index, parts);
      console.log('Parsed headData:', headData);
      console.log('Parsed itemData length:', itemData.length);
      console.log('First few itemData entries:', itemData.slice(0, 3));
      if (itemData.length === 0) {
        alert('No valid item data found in the file.');
        return;
      }

      // Construct payload for API
      const payload = {
        GrnNo: record.gipNo,
        GrnId: parts[index].Gipid,
        UserName: sessionStorage.getItem('userName'),
        GrnDate: record.gipDate,
        PartNo: headData.PartNo || parts[index].partNo,
        PartName: headData.PartName || '',
        LotQty: headData.LotQty,
        BatchNo: headData.BatchNo,
        InspectionQty: headData.InspectionQty,
        RejectionQty: headData.RejectionQty,
        SourceFile: file.name,
        FileContent: fileContent,
        Items: itemData,
        InspectionDate: new Date().toISOString(),
      };

      console.log('Payload for API:', payload);

      // Store the file in the assets folder using File System Access API
      // Store the file in the assets folder using Node local save endpoint
      try {
        const qcSeqId = parts[index].QcSeqId || parts[index].Itemid;
        if (!qcSeqId && qcSeqId !== 0) {
          alert("❌ QcSeqId or Itemid is missing for this part. Cannot upload file.");
          return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("QcSeqId", qcSeqId);
        formData.append("GipId", Gipid);

        console.log("🔍 Debug: Sending file upload request");
        console.log("🔍 Debug: QcSeqId:", qcSeqId);
        console.log("🔍 Debug: File name:", file.name);
        console.log("🔍 Debug: File size:", file.size);

        const res = await fetch("https://spflqc.revampaioffice.com/api/upload.php", {
  method: "POST",
  body: formData
});
        console.log("🔍 Debug: Fetch response status:", res.status);
        console.log("🔍 Debug: Fetch response ok:", res.ok);

        if (!res.ok) {
          throw new Error(`Local upload failed with status ${res.status}`);
        }

        const result = await res.json();
        console.log("🔍 Debug: Server response:", result);

        if (result.success) {
          // update UI file reference
          const updated = [...parts];
          updated[index].uploadedFile = result.filePath;
          setParts(updated);

          try {
            const apiResult = await api.uploadInspectionReport(payload);
            console.log('API Response:', apiResult);
            alert(`✅ Success: ${apiResult.message || 'Data uploaded successfully.'}`);
          } catch (apiError) {
            console.error('API Error:', apiError);
            alert('✅ File uploaded locally, but API failed. Please try again.');
          }
        } else {
          alert("❌ Failed to save file locally.");
        }
      } catch (error) {
        console.error("Error saving file:", error);
        alert("❌ Error saving file locally.");
      }

      // Optionally update the part with parsed data
      const item = itemData[0];
      const updatedParts = [...parts];
      updatedParts[index] = {
        ...updatedParts[index],
        description: item.Description || updatedParts[index].description,
        observations: item,
      };
      setParts(updatedParts);

    } catch (error) {
      console.error('Error inserting data:', error);
      alert('Error: ' + (error.message || 'Unknown error'));
    }
  };

  // Build payload & call StartInspectionEntry_API (same name you used)
  const handleUpdate = async () => {
    try {
      const databaseKey = sessionStorage.getItem("databaseKey");
      const UsrName = sessionStorage.getItem("userName");
      const QcRefNo = qcNo;
      const QcRefdate = qcDate;
      const QcById = checkedBy?.value || 0;
      const QcByName = checkedBy?.label || "";
      const SupervisorId = supervisor?.value || 0;
      const SupervisorName = supervisor?.label || "";

      if (!QcRefNo || !QcRefdate) {
        alert("❌ Please enter QC No and QC Date!");
        return;
      }
      if (!QcByName) {
        alert("❌ Please select Checked By!");
        return;
      }

      setLoading(true);

      // Build details array for API - include hidden ids where necessary
      const details = parts.map(p => ({
        QcId: p.QcId,
        Gipid: p.Gipid,
        QcSeqId: p.QcSeqId,
        PoId: p.PoId,
        Itemid: p.Itemid,
        ReceivedQty: p.receivedQty,
        AcceptedQty: p.acceptedQty,
        RejectionDetails: p.rejectionDetails,
        AssetInfo: p.assetInfo,
        Upload: p.uploadedFile
      }));

      const bodyParams = {
        Gipid,
        QcRefNo,
        QcRefdate,
        QcById,
        QcByName,
        SupervisorId,
        Supervisor: SupervisorName,
        UsrName,
        Details: details
      };

      const response = await api.StartInspectionEntry_API(bodyParams, databaseKey);
      console.log("✅ API Response:", response);
      alert("✅ QC Entry Updated Successfully!");

      // reload parts/headers after update
      await fetchQCDetails();
    } catch (error) {
      console.error("❌ Error updating inspection entry:", error);
      alert("❌ Error updating inspection entry. Please check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPage = () => navigate(-1);

  // drag scrolling handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // only left click for drag
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - tableContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // scroll speed multiplier
    tableContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // context menu handler
  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY
    });
  };

  // Handle QC Entry Finished
  const handleQcFinished = async () => {
    if (!allConditionsMet) {
      alert("❌ All save conditions must be met for all parts before finishing QC entry.");
      return;
    }

    try {
      const databaseKey = sessionStorage.getItem("databaseKey");
      const userName = sessionStorage.getItem("userName");

      const response = await api.QcgrnFinishedUpadate_API(Gipid, userName);

      alert("✅ QC Entry Finished Successfully!");
      navigate(-1); // Go back or to appropriate page
    } catch (error) {
      console.error("❌ Error finishing QC entry:", error);
      alert("❌ Failed to finish QC entry. Check console for details.");
    }
  };

  // helper to render cell value safely
  const getCellValue = (item, accessor) => {
    if (!item) return "";
    const key = headerMap[accessor] || accessor;
    const v = item[key];
    if (key === "receivedQty" || key === "acceptedQty" || key === "qcRejQty" || key === "UnitPrice" || key === "Amount") {
      return to3(v);
    }
    // for rejectionDetails object, show summary
    if (key === "rejectionDetails") {
      if (!v) return "";
      if (typeof v === "object") {
        return v.remarks ? `${v.type || ""} (${v.qty || ""}) - ${v.remarks}` : `${v.type || ""} (${v.qty || ""})`;
      }
      return String(v);
    }
    return v ?? "";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto bg-white rounded-lg shadow-lg">
        <style>
          {`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type="number"] {
              -moz-appearance: textfield;
            }
          `}
        </style>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white p-4 rounded-t-lg flex items-center">
          {!isViewOnly && (
            <button
              onClick={handleCancelPage}
              className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-3 py-2 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm mr-4"
              title="Go Back"
            >
              <span>⬅️</span>
              <span>Back</span>
            </button>
          )}
          <h1 className="text-xl font-bold flex-1 text-center">Edit QC Entry</h1>
        </div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-pulse text-xl text-slate-600 font-semibold">🔄 Loading data...</div>
            </div>
          ) : (
            <>
              {/* Header section */}
              <div className="p-3 sm:p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {[{ label: "GIP No", value: record.gipNo },
            { label: "GIP Date", value: record.gipDate, type: "date" },
            { label: "Supplier", value: record.supName },
            { label: "DC No", value: record.dcNo },
            { label: "DC Date", value: record.dcDate, type: "date" }].map((f, i) => (
              <div key={i}>
                <label className="text-xs text-gray-600 block mb-0.5">{f.label}</label>
                <input type={f.type || "text"} className="border p-2 sm:p-1.5 text-sm w-full rounded bg-gray-50" value={f.value || ""} readOnly />
              </div>
            ))}

            <div>
              <label className="text-xs text-gray-600 block mb-0.5">QC No</label>
              <input type="text" className="border p-2 sm:p-1.5 text-sm w-full rounded" value={qcNo} onChange={(e) => setQcNo(e.target.value)} />
            </div>

            <div>
              <label className="text-xs text-gray-600 block mb-0.5">QC Date</label>
              <input type="date" className="border p-2 sm:p-1.5 text-sm w-full rounded" value={qcDate} onChange={(e) => setQcDate(e.target.value)} />
            </div>

            <div>
              <label className="text-xs text-gray-600 block mb-0.5">Prepared By</label>
              <input type="text" className="border p-2 sm:p-1.5 text-sm w-full rounded bg-gray-100 cursor-not-allowed" value={preparedBy?.label || ""} readOnly />
            </div>

            <div>
              <label className="text-xs text-gray-600 block mb-0.5">Checked By</label>
              <Select
                options={users}
                value={checkedBy}
                onChange={setCheckedBy}
                placeholder="Select user"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "32px",
                    fontSize: "12px",
                  }),
                }}
              />
            </div>

            <div>
              <label className="text-xs text-gray-600 block mb-0.5">Supervisor</label>
              <Select
                options={supervisors.length ? supervisors : users}
                value={supervisor}
                onChange={setSupervisor}
                placeholder="Select supervisor"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "32px",
                    fontSize: "12px",
                  }),
                }}
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-1" />
            <div>
              {!isViewOnly && (
                <button onClick={handleUpdate} className="bg-green-600 text-white px-3 py-2 sm:px-4 rounded hover:bg-green-700 w-full sm:w-auto text-sm sm:text-base">
                  Update
                </button>
              )}
            </div>
          </div>

          {/* Parts Table */}
          <div className="p-3 sm:p-4 lg:p-6 mb-5">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-semibold text-gray-700">QC Parts List</h3>
                <button
                  onClick={() => setShowColumnModal(true)}
                  className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600"
                  title="Column settings"
                >
                  <CogIcon className="w-4 h-4" />
                </button>
              </div>
              {!isViewOnly && (
                <button
                  onClick={handleQcFinished}
                  disabled={!allConditionsMet}
                  className={`px-3 py-2 sm:px-4 rounded text-white text-xs sm:text-sm w-full sm:w-auto ${allConditionsMet
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                    }`}
                >
                  QC Entry Finished
                </button>
              )}
            </div>

            <div
              ref={tableContainerRef}
              className="overflow-x-auto shadow-lg sm:shadow-2xl rounded-xl border border-gray-200"
              style={{
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              <div className="max-h-[60vh] sm:max-h-[70vh] lg:max-h-[75vh] overflow-y-auto">
                <table className="min-w-full bg-white border-collapse" onContextMenu={handleContextMenu}>
                <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                  <tr>
                    {visibleHeaders.map((h) => (
                      <th key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-bold text-white border-r border-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parts.length > 0 ? (
                    parts.map((item, i) => (
                      <tr key={i} className={`transition-all duration-200 border-b border-slate-200 ${i % 2 === 0 ? 'bg-white hover:bg-slate-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                        {visibleHeaders.map((h) => {
                          const accessor = headerMap[h];

                          // ✅ Accepted Qty Column
                          if (h === "Accepted") {
                            const received = parseFloat(item.receivedQty) || 0;
                            const accepted = parseFloat(item.acceptedQty) || 0;
                            const hasError = accepted > received;

                            return (
                              <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 min-w-[100px] text-right border-r border-slate-200">
                                <div className="flex flex-col">
                                  <input
                                    type="text"
                                    value={item.acceptedQty}
                                    disabled={isViewOnly || allNewParts}
                                    onChange={(e) => {
                                      let val = e.target.value;
                                      if (val === "" || /^[0-9]*\.?[0-9]{0,3}$/.test(val)) {
                                        const updated = [...parts];
                                        updated[i].acceptedQty = val;

                                        const received = parseFloat(updated[i].receivedQty) || 0;
                                        const accepted = parseFloat(val) || 0;

                                        updated[i].qcRejQty = (received - accepted).toFixed(3);

                                        updated[i].updated = true; // mark as unsaved → button turns red
                                        setParts(updated);
                                      }
                                    }}
                                    onBlur={() => {
                                      const updated = [...parts];
                                      let val = parseFloat(updated[i].acceptedQty);
                                      if (isNaN(val)) val = 0;

                                      const received = parseFloat(updated[i].receivedQty);
                                      if (val > received) val = received;

                                      updated[i].acceptedQty = val.toFixed(3);
                                      updated[i].qcRejQty = (received - val).toFixed(3);

                                      setParts(updated);
                                    }}
                                    className={`border w-full text-right text-xs sm:text-sm rounded px-2 py-1 ${hasError ? "border-red-500 bg-red-50" : "border-gray-300 bg-white"}`}
                                  />
                                  {hasError && (
                                    <span className="text-red-600 text-xs mt-1">Cannot exceed received qty</span>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          // ✅ Rejected Qty Column
                          if (h === "Rej Qty") {
                            return (
                              <td
                                key={h}
                                className={`px-2 sm:px-4 py-2 sm:py-3 text-right font-semibold border-r border-slate-200 text-xs sm:text-sm ${parseFloat(item.qcRejQty) > 0
                                    ? "text-red-600"
                                    : "text-green-600"
                                  }`}
                              >
                                {to3(item.qcRejQty)}
                              </td>
                            );
                          }


          if (h === "Attachments") {
            return (
              <td className="px-2 sm:px-4 py-2 sm:py-3 font-mono border-r border-slate-200">
                <Attachments
                  mdlId="Item_Master"
                  primeId={item.Itemid}
                  primeName={item.PartNo}
                  uploadedBy={localStorage.getItem("username") || "UnknownUser"}
                  onAttachmentsLoaded={(count) => setAttachmentCounts(prev => ({...prev, [item.Itemid]: count}))}
                  disabled={isViewOnly || allNewParts}
                />
              </td>
            );
          }

                       
                        
                          // ✅ Upload Button
                          if (h === "Upload") {
                            return (
                              <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-center flex flex-row items-center space-x-1 border-r border-slate-200">
                                {!isViewOnly && !allNewParts && (
                                  <>
                                    <label className="bg-blue-500 text-white px-1 sm:px-2 py-0.5 rounded text-xs cursor-pointer hover:bg-blue-600 transition">
                                      Choose File
                                      <input type="file" onChange={(e) => handleFileChange(e, i)} accept=".xlsx,.xls" className="hidden" />
                                    </label>
                                    <button onClick={() => handleInsert(i)} className="bg-green-500 text-white px-1 sm:px-2 py-0.5 rounded text-xs hover:bg-green-600 transition">Upload</button>
                                  </>
                                )}
                              </td>
                            );
                          }

                         // ✅ Uploaded File Column (clickable download)
// ✅ Uploaded File Column (auto-loaded + clickable)
if (h === "Uploaded File") {
  return (
    <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-center border-r border-slate-200 text-xs sm:text-sm">
      {item.uploadedFile ? (
        <button
          onClick={() => handleDownload(item.uploadedFile)}
          className="text-blue-600 hover:underline"
          title="Click to download"
        >
          {item.uploadedFile.split("/").pop()}
        </button>
      ) : (
        <span className="text-gray-400">No file uploaded</span>
      )}
    </td>
  );
}

                          // ✅ Asset Info Button
                          if (h === "Asset Info") {
                            return (
                              <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-center border-r border-slate-200 text-xs sm:text-sm">
                                {!allNewParts && (!isViewOnly || item.assetInfo) && (
                                  <button
                                    onClick={() => openVerifyPopup(i)}
                                    className={`font-medium ${item.assetInfo === "Verified"
                                        ? "text-green-600 hover:text-green-700"
                                        : "text-blue-600 hover:text-blue-700"
                                      }`}
                                    disabled={isViewOnly && !item.assetInfo} // Disable if view-only and no data
                                  >
                                    {isViewOnly ? (item.assetInfo || "Not Set") : (item.assetInfo || "Not Verified")}
                                  </button>
                                )}
                              </td>
                            );
                          }

                          // ✅ Rejection Details Button
                          if (h === "Rejection Details") {
                            return (
                              <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-center border-r border-slate-200">
                                {!allNewParts && (
                                  <button
                                    onClick={() => openRejectionPopup(i)}
                                    className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium ${item.rejectionDetails
                                        ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                      }`}
                                    disabled={isViewOnly && !item.rejectionDetails && !parseFloat(item.qcRejQty || 0)} // Disable if view-only, no data, and no rej qty
                                  >
                                    {item.rejectionDetails ? "View" : "Add"}
                                  </button>
                                )}
                              </td>
                            );
                          }

                          // ✅ Save Button
                           if (h === "Save") {
                             const rejQty = parseFloat(item.qcRejQty) || 0;
                             const hasRejectionDetails = !!item.rejectionDetails;
                             const rejectionCondition = rejQty <= 0 || hasRejectionDetails;
                             const attachmentCount = attachmentCounts[item.Itemid] || 0;
                             const hasUploadedFile = !!item.uploadedFile;
                             const attachmentCondition = item.InwardInsp === 1 ? hasUploadedFile : true;
                             const canSave = item.assetInfo === "Verified" && rejectionCondition && attachmentCondition;

                             return (
                               <td key={h} className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                                 {!isViewOnly && (
                                   <button
                                     onClick={() => handleSave(item, i)}
                                     disabled={!canSave}
                                     className={`p-1 sm:p-2 rounded-md transition text-white ${!canSave
                                         ? "bg-gray-400 cursor-not-allowed"
                                         : item.updated
                                           ? "bg-red-600 hover:bg-red-700" // 🔴 Show red if there are unsaved changes
                                           : "bg-green-600 hover:bg-green-700" // 🟢 Show green after saving
                                       }`}
                                     title={!canSave ? "Conditions not met" : item.updated ? "Unsaved changes" : "Saved"}
                                   >
                                     <CheckIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                                   </button>
                                 )}
                               </td>
                             );
                           }

                          // ✅ Default Column
                          return (
                            <td key={h} className={`px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 border-r border-slate-200 text-center ${h === 'Description' ? 'min-w-[150px]' : h === 'Part No' ? 'min-w-[120px]' : h === 'Uploaded File' ? 'min-w-[150px]' : h === 'Asset Info' ? 'min-w-[120px]' : h === 'Rejection Details' ? 'min-w-[120px]' : 'min-w-[80px]'}`}>
                              {getCellValue(item, h)}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleHeaders.length}
                        className="px-4 sm:px-6 py-8 sm:py-12 text-center text-slate-500 bg-slate-100 text-sm sm:text-base"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <span className="text-2xl sm:text-4xl">📭</span>
                          <span>No parts found</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* Asset Verify Popup */}
          {showPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-2xl p-4 sm:p-5 w-full max-w-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 text-center">Verify / Edit Asset Info</h3>
                <div className="space-y-2 text-sm">
                  <label className="text-xs text-gray-600 mb-0.5 block">Asset</label>
                  {assetOptions.length > 1 ? (
                    <Select
                      options={assetOptions}
                      value={selectedAsset}
                      onChange={setSelectedAsset}
                      placeholder="Select Asset"
                      isDisabled={isViewOnly}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "28px",
                          fontSize: "12px",
                        }),
                      }}
                    />
                  ) : (
                    <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedAsset?.label || "N/A"} readOnly />
                  )}
                  <label className="text-xs text-gray-600 mb-0.5 block">Mandrel</label>
                  {mandrelOptions.length > 1 ? (
                    <Select
                      options={mandrelOptions}
                      value={selectedMandrel}
                      onChange={setSelectedMandrel}
                      placeholder="Select Mandrel"
                      isDisabled={isViewOnly}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "28px",
                          fontSize: "12px",
                        }),
                      }}
                    />
                  ) : (
                    <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedMandrel?.label || "N/A"} readOnly />
                  )}
                  <label className="text-xs text-gray-600 mb-0.5 block">Die</label>
                  {dieOptions.length > 1 ? (
                    <Select
                      options={dieOptions}
                      value={selectedDie}
                      onChange={setSelectedDie}
                      placeholder="Select Die"
                      isDisabled={isViewOnly}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "28px",
                          fontSize: "12px",
                        }),
                      }}
                    />
                  ) : (
                    <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedDie?.label || "N/A"} readOnly />
                  )}
                </div>
                <div className="flex justify-end gap-2 sm:gap-3 mt-4">
                  <button onClick={() => setShowPopup(false)} className="bg-red-500 text-white px-2 sm:px-3 py-1.5 rounded hover:bg-red-600 text-sm">Cancel</button>
                  {!isViewOnly && <button onClick={handleSavePopup} className="bg-green-600 text-white px-2 sm:px-3 py-1.5 rounded hover:bg-green-700 text-sm">Save</button>}
                </div>
              </div>
            </div>
          )}

          {/* Rejection Popup (new table-style modal) */}
          {showRejPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start pt-10 sm:pt-20 z-50 p-4">
              <div className="bg-white p-4 sm:p-6 rounded shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-700">Rejection Details</h3>
                  <div className="text-xs sm:text-sm">
                    Total Rej Qty: {parseFloat(qcRejQtyLimit).toFixed(3)}
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs sm:text-sm">
                    {!isViewOnly && <button onClick={addRejRow} className="bg-blue-500 text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm hover:bg-blue-600">Add</button>}
                  </div>
                </div>

                <div className="text-xs text-gray-600 mb-2">Click <span className="font-medium">Delete</span> to remove a row. Use the spin control for quantities.</div>

                <div className="overflow-auto min-h-[200px] sm:min-h-[300px]">
                  <table className="w-full table-fixed text-xs sm:text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-gray-700">
                        <th className="w-12 sm:w-16 border p-1 sm:p-2 text-left text-xs sm:text-sm">Action</th>
                        <th className="w-20 sm:w-28 border p-1 sm:p-2 text-right text-xs sm:text-sm">Rej.Qty</th>
                        <th className="border p-1 sm:p-2 text-left text-xs sm:text-sm">Remarks</th>
                        <th className="w-32 sm:w-64 border p-1 sm:p-2 text-left text-xs sm:text-sm">Rejection Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejRows.map((r) => (
                        <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border p-1 sm:p-2 align-top">
                            {!isViewOnly && (
                              <button
                                onClick={() => deleteRejRow(r.id)}
                                className="text-blue-600 hover:underline text-xs sm:text-sm"
                              >
                                Delete
                              </button>
                            )}
                          </td>

                          <td className="border p-1 sm:p-2 align-top text-right">
                            <input
                              type="text"
                              className="border rounded px-1 sm:px-2 py-1 w-full text-right text-xs sm:text-sm"
                              value={r.qty}
                              disabled={isViewOnly}
                              onWheel={(e) => e.preventDefault()}
                              onChange={(e) => {
                                const val = e.target.value;

                                // Basic format validation
                                if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                  const num = parseFloat(val) || 0;
                                  // Compute current total excluding this row
                                  const otherTotal = parseFloat(rejRows
                                    .filter(x => x.id !== r.id)
                                    .reduce((sum, x) => sum + (parseFloat(x.qty) || 0), 0).toFixed(3));

                                  const currentNum = parseFloat(num.toFixed(3));
                                  const limit = parseFloat(qcRejQtyLimit.toFixed(3));

                                  // Check total limit
                                  if (otherTotal + currentNum > limit) {
                                    alert(
                                      `Total Rej.Qty (${(otherTotal + currentNum).toFixed(3)}) cannot exceed QC Rej.Qty (${limit.toFixed(3)}).`
                                    );
                                    // Auto adjust to remaining qty
                                    const remaining = Math.max(limit - otherTotal, 0);
                                    updateRejRow(r.id, "qty", remaining.toFixed(3));
                                  } else {
                                    updateRejRow(r.id, "qty", val);
                                  }
                                }
                              }}
                            />
                          </td>


                          <td className="border p-1 sm:p-2 align-top">
                            <input
                              type="text"
                              className="border rounded px-1 sm:px-2 py-1 w-full text-xs sm:text-sm"
                              value={r.remarks}
                              disabled={isViewOnly}
                              onChange={(e) => updateRejRow(r.id, "remarks", e.target.value)}
                            />
                          </td>

                          <td className="border p-1 sm:p-2 align-top">
                            <Select
                              options={rejectionTypesOptions}
                              value={r.type}
                              onChange={(val) => updateRejRow(r.id, "type", val)}
                              placeholder="Select type"
                              isDisabled={isViewOnly}
                              styles={{
                                control: base => ({ ...base, minHeight: "30px", fontSize: "12px" }),
                                menu: base => ({ ...base, zIndex: 9999 }),
                                menuList: base => ({ ...base, maxHeight: "150px" })
                              }}
                            />
                          </td>
                        </tr>
                      ))}

                      {rejRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-4 text-center text-gray-500">No rejection rows. Click Add to create one.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-2 sm:gap-3 mt-4">
                  <button onClick={() => setShowRejPopup(false)} className="bg-red-500 text-white px-2 sm:px-3 py-1.5 rounded hover:bg-red-600 text-sm">Cancel</button>
                  {!isViewOnly && (
                    <button
                      onClick={handleSaveRejection}
                      disabled={rejRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0).toFixed(3) !== qcRejQtyLimit.toFixed(3)}
                      className={`px-2 sm:px-3 py-1.5 rounded text-white text-sm ${rejRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0).toFixed(3) === qcRejQtyLimit.toFixed(3) ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Context Menu for Column Visibility */}
      {contextMenu.visible && (
        <div
          className="fixed bg-white border border-gray-300 rounded shadow-lg p-2 z-50 context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="text-xs font-semibold mb-2">Show Columns:</div>
          {extraColumns.map(col => (
            <label key={col} className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1">
              <input
                type="checkbox"
                checked={visibleColumns[col]}
                onChange={(e) => setVisibleColumns(prev => ({ ...prev, [col]: e.target.checked }))}
                className="w-3 h-3"
              />
              <span>{col}</span>
            </label>
          ))}
        </div>
      )}

      {/* Column Modal */}
      {showColumnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md modal-content">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">Column Visibility</h3>
            <div className="space-y-3">
              {extraColumns.map(col => (
                <label key={col} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleColumns[col]}
                    onChange={(e) => setVisibleColumns(prev => ({ ...prev, [col]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{col}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowColumnModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


